import { faInbox, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AgGridAtom } from "../../../../components/atoms/atom";
import ModalAtom from "../../../../components/atoms/modal/modal";
import { useSaveRejectedInvoiceMutation } from "../../../../store/services/invoiceService";
import { useGetProjectTicketCategoriesWithLVStatusQuery } from "../../../../store/services/ticketCategoriesService";
import {
    fetchPreviousInvoicesV2,
    selectFormState,
    selectIsRejectedInvoice,
    selectTicketCategoryWorkflowState,
    setFormField,
    setIsRejectedInvoice,
    setIsSubmitting,
    setSelectionError
} from "../../../../store/slices/invoiceV2Slice";
import { showToast } from "../../../../utils/method";
import CategorySelector from "./CategorySelector";
import { getHistoricalInvoicesColumnDefs } from "./invoiceSharedUtils";
import InvoiceWarnings from "./InvoiceWarnings";
import LoadingIndicator from "./LoadingIndicator";
import PreviousInvoicesTable from "./PreviousInvoicesTable";
import RejectedInvoiceForm from "./RejectedInvoiceForm";
import "./TicketCategorySelection.scss";

const TicketCategorySelection = memo(({
    projectId,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [internalDisabled, setInternalDisabled] = useState(disabled);

    // State for historical invoices modal
    const [showHistoricalModal, setShowHistoricalModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Redux selectors with optimized workflow state
    const formValues = useSelector(selectFormState);
    const isRejectedInvoice = useSelector(selectIsRejectedInvoice);
    const {
        data: rawCategoriesResponse,
        isLoading,
    } = useGetProjectTicketCategoriesWithLVStatusQuery(projectId, {
        skip: !projectId,
    });
    // Use the optimized combined selector
    const {
        previousInvoices,
        loading: loadingInvoices,
        error: selectionError,
        existingDraft: existingDraftInvoice,
        hasUnchecked: hasUncheckedInvoices,
        isSubmitting
    } = useSelector(selectTicketCategoryWorkflowState);

    const [saveRejectedInvoice] = useSaveRejectedInvoiceMutation();

    const ticketCategoryOptions = useMemo(() => {
        return (rawCategoriesResponse?.items ?? []).map((cat) => ({
            value: cat.id,
            label: cat.name,
            // Gate on hasReadyLV (main LV with synced positions), not hasLV: a
            // BoQ-editor main LV exists as soon as it's saved as a draft, but has
            // no invoiceable positions until it is finalized.
            isDisabled: !cat.hasReadyLV || cat.hasFinalInvoice,
        }));
    }, [rawCategoriesResponse?.items]);

    // Use shared column definitions for historical invoices grid
    const historicalColumnDefs = useMemo(() => getHistoricalInvoicesColumnDefs(t), [t]);

    useEffect(() => {
        setInternalDisabled(disabled);
    }, [disabled]);

    // Fetch previous invoices when ticket category changes
    useEffect(() => {
        if (formValues.selectedTicketCategory && !isRejectedInvoice) {
            dispatch(fetchPreviousInvoicesV2({
                projectId,
                ticketCategoryId: formValues.selectedTicketCategory.value,
                currentInvoiceId: formValues.id,
            }));
        }
    }, [dispatch, formValues.selectedTicketCategory, formValues.id, projectId, isRejectedInvoice]);

    // Memoized change handler with comprehensive logic from V1
    const handleTicketCategoryChange = useCallback((e) => {
        if (internalDisabled) { return; }

        // Reset rejected invoice state when changing categories
        dispatch(setIsRejectedInvoice(false));

        const selectedId = e.target.value;

        if (!selectedId) {
            dispatch(setSelectionError("invoice.error-ticket-category-required"));
            dispatch(setFormField({
                field: "selectedTicketCategory",
                value: null,
                skipDirtyFlag: true,
            }));
            return;
        }

        // Use string comparison for better performance
        const selectedCategory = ticketCategoryOptions.find(
            (cat) => String(cat.value) === selectedId,
        );

        // For rejected invoices, we can select categories without LVs
        if (selectedCategory?.isDisabled && !isRejectedInvoice) {
            dispatch(setSelectionError("invoice.error-ticket-category-no-lv"));
            return;
        }

        dispatch(setSelectionError(""));
        dispatch(setFormField({
            field: "selectedTicketCategory",
            value: selectedCategory || null,
            skipDirtyFlag: true,
        }));

        // Previous invoices will be fetched by the useEffect when formValues.selectedTicketCategory updates
    }, [internalDisabled, ticketCategoryOptions, isRejectedInvoice, dispatch]);

    // Handle viewing historical invoices from previous invoices table
    const handleViewHistoricalInvoices = useCallback((invoice) => {
        setSelectedInvoice(invoice);
        setShowHistoricalModal(true);
    }, []);

    // Handle closing historical modal
    const handleCloseHistoricalModal = useCallback(() => {
        setShowHistoricalModal(false);
        setSelectedInvoice(null);
    }, []);

    // Handle submission of the rejected invoice form
    const handleRejectedInvoiceSubmit = async (invoiceData) => {
        try {
            dispatch(setIsSubmitting(true));

            // RTK mutation: query strips projectId for the URL and spreads
            // the remaining body fields flat - matches the backend's flat
            // Joi schema for POST /projects/:projectId/rejected-invoices.
            await saveRejectedInvoice({
                projectId,
                ...invoiceData,
            }).unwrap();

            showToast(t("invoice.rejected-invoice-created"), {
                type: "success",
            });

            // Redirect to the invoice list
            navigate(`/projects/${projectId}/invoice`);
        } catch (error) {
            // console.error("Error creating rejected invoice:", error);
            // showToast(t("invoice.error-creating-rejected-invoice"), {
            //     type: "error",
            // });
        } finally {
            dispatch(setIsSubmitting(false));
        }
    };

    // Memoized loading component
    const LoadingComponent = useMemo(() => (
        <Card className="mb-4">
            <Card.Header className="bg-light">
                <h5 className="mb-0">
                    {t("invoice.select-ticket-category")}
                </h5>
            </Card.Header>
            <Card.Body>
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">
                            {t("common.loading")}
                        </span>
                    </div>
                    <p className="mt-2">{t("invoice.loading-categories")}</p>
                </div>
            </Card.Body>
        </Card>
    ), [t]);

    // Early loading return with memoized component
    if (isLoading) {
        return LoadingComponent;
    }

    return (
        <div className="ticket-category-selection">
            <div className="ticket-category-selection__selector">
                <CategorySelector
                    value={formValues.selectedTicketCategory?.value || ""}
                    options={ticketCategoryOptions}
                    onChange={handleTicketCategoryChange}
                    error={selectionError ? t(selectionError) : ""}
                    disabled={internalDisabled}
                />
            </div>

            {formValues.selectedTicketCategory && !isRejectedInvoice && (
                <div className="ticket-category-selection__content">
                    {!internalDisabled && !formValues.readonly && (
                        <div className="ticket-category-selection__warnings">
                            <InvoiceWarnings
                                existingDraftInvoice={existingDraftInvoice}
                                hasUncheckedInvoices={hasUncheckedInvoices}
                                previousInvoices={previousInvoices}
                                projectId={projectId}
                                selectedTicketCategory={formValues.selectedTicketCategory}
                                isRejectedInvoice={isRejectedInvoice}
                            />
                        </div>
                    )}
                    <div className="ticket-category-selection__table-section">
                        <h6 className="ticket-category-selection__table-header d-flex align-items-center">
                            {t("invoice.previous-invoices")}
                            {previousInvoices.length > 0 && (
                                <span className="badge bg-secondary ms-2">
                                    {previousInvoices.length}
                                </span>
                            )}
                        </h6>

                        <div className="ticket-category-selection__table-body">
                            {loadingInvoices ? (
                                <LoadingIndicator />
                            ) : (
                                <PreviousInvoicesTable
                                    invoices={previousInvoices}
                                    onHistoricalDataClick={handleViewHistoricalInvoices}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {formValues.selectedTicketCategory && isRejectedInvoice && (
                <div className="ticket-category-selection__rejected-form">
                    <RejectedInvoiceForm
                        ticketCategory={formValues.selectedTicketCategory}
                        onSubmit={handleRejectedInvoiceSubmit}
                        isSubmitting={isSubmitting}
                    />
                </div>
            )}

            {/* Historical Invoices Modal */}
            <ModalAtom
                show={showHistoricalModal}
                handleClose={handleCloseHistoricalModal}
                title={`${selectedInvoice?.invoiceNumber || t("invoice.invoice")} - ${t("invoice.historical-invoices-details")}`}
                size="xl"
                centered
                hideFooter
                body={
                    <>
                        <div className="alert alert-info">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            {t("invoice.historical-data-description")}
                        </div>
                        {selectedInvoice?.historicalInvoices && selectedInvoice.historicalInvoices.length > 0 ? (
                            <div className="mb-3 tcs-grid-container">
                                <AgGridAtom
                                    columnDefs={historicalColumnDefs}
                                    rowData={selectedInvoice.historicalInvoices}
                                    domLayout="normal"
                                    rowHeight={56}
                                />
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <FontAwesomeIcon icon={faInbox} className="fs-1" />
                                <p className="mt-2">{t("invoice.no-historical-invoices")}</p>
                            </div>
                        )}
                    </>
                }
            />
        </div>
    );
});

TicketCategorySelection.displayName = "TicketCategorySelection";

export default TicketCategorySelection;
